import { fromDate } from "@internationalized/date";
import { ChevronsUpDown, Plus } from "lucide-react";

import { Badge } from "@workspace/ui/components/badge";
import { Button } from "@workspace/ui/components/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@workspace/ui/components/dropdown-menu";
import { useIsMobile } from "@workspace/ui/hooks/use-mobile";
import { cn } from "@workspace/ui/lib/utils";

export function TenantSwitcher({
	tenants,
	tenant,
	setActiveTenant,
	size = "lg",
}: {
	setActiveTenant: (key: string) => Promise<void>;
	size?: "sm" | "lg";
	tenant: string | null;
	tenants: {
		label: string;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		logo?: any;
		plan: string;
		key: string;
		lastModified: string;
		createdAt: string | null;
	}[];
}) {
	const isMobile = useIsMobile();

	const empty = tenants[0]
		? {
				label: tenants[0].label,
				logo: tenants[0].logo,
				plan: "Free",
				key: tenants[0].key,
				lastModified: tenants[0].lastModified,
			}
		: {
				label: tenant,
				logo: undefined,
				plan: "Free",
				key: tenant,
				lastModified: undefined,
			};

	const activeTeam = tenants.find((team) => team.key === tenant) || empty;

	return (
		<DropdownMenu>
			<DropdownMenuTrigger
				disabled={tenants.length < 2 && tenant !== "empty"}
				render={
					<Button
						size={size}
						variant="outline"
						className="data-popup-open:bg-sidebar-accent data-popup-open:text-sidebar-accent-foreground"
					>
						{!!activeTeam.logo && (
							<div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
								<activeTeam.logo className="size-4" />
							</div>
						)}
						<div className="grid flex-1 text-left text-sm/tight">
							<span className="truncate font-semibold">{activeTeam.label}</span>
						</div>
						<ChevronsUpDown className="ml-auto" />
					</Button>
				}
			/>
			<DropdownMenuContent
				className="max-h-[calc(50vh)] min-w-44 overflow-scroll rounded-lg"
				align="start"
				side={isMobile ? "bottom" : "right"}
				sideOffset={4}
			>
				<DropdownMenuLabel className="text-xs text-muted-foreground">
					Organisations
				</DropdownMenuLabel>
				{tenants
					.sort(
						(a, b) =>
							new Date(b.createdAt ?? 0).getTime() -
							new Date(a.createdAt ?? 0).getTime(),
					)
					.map((team) => {
						const isNew =
							fromDate(new Date(), "utc")
								.add({
									hours: -1,
								})
								.compare(fromDate(new Date(team.lastModified), "utc")) <= 0;

						return (
							<DropdownMenuItem
								key={team.key}
								onClick={() => setActiveTenant(team.key)}
								className={cn("gap-2 p-2", {
									"bg-accent/50": team.key === activeTeam.key,
								})}
							>
								{team.logo && (
									<div className="flex size-6 items-center justify-center rounded-sm border">
										<team.logo className="size-4 shrink-0" />
									</div>
								)}
								{team.label}
								{isNew && <Badge className="ml-auto">New</Badge>}
							</DropdownMenuItem>
						);
					})}
				<DropdownMenuSeparator className="hidden" />
				<DropdownMenuItem className="hidden gap-2 p-2">
					<div className="flex size-6 items-center justify-center rounded-md border bg-background">
						<Plus className="size-4" />
					</div>
					<div className="font-medium text-muted-foreground">Add team</div>
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
